import { Modal, Spin, Descriptions, Typography, Table } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

import { useLazyGetLogByIdQuery, type LoggerLogDto } from '@app/api/Logger';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';

import type { ColumnsType } from 'antd/es/table';

const { Text, Paragraph } = Typography;

interface LogModalProps {
  open: boolean;
  logId: number | null;
  onClose: () => void;
}

interface PropertyRow {
  key: string;
  name: string;
  value: unknown;
}

export const EmailLogModal: React.FC<LogModalProps> = ({
  open,
  logId,
  onClose,
}) => {
  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const { showNotification } = useNotification();

  const [log, setLog] = useState<LoggerLogDto | null>(null);

  const [getLogById, { isLoading }] = useLazyGetLogByIdQuery();

  useEffect(() => {
    if (!open || !logId) return;
    const openLog = async () => {
      try {
        const result = await getLogById({ ActiveBranchId, id: logId }).unwrap();
        setLog(result);
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    };

    openLog();
  }, [open, logId, getLogById, ActiveBranchId, showNotification]);

  const propertiesData = log?.Properties
    ? Object.entries(log.Properties).map(([key, value]) => ({
        key,
        name: key,
        value: value ?? '—',
      }))
    : [];

  const columns: ColumnsType<PropertyRow> = [
    {
      title: 'Key',
      dataIndex: 'name',
      key: 'name',
      width: '40%',
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      render: (value) =>
        typeof value === 'object' ? (
          <pre style={{ margin: 0 }}>{JSON.stringify(value, null, 2)}</pre>
        ) : (
          String(value)
        ),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={onClose}
      title="Детали лога"
      width={800}
    >
      <Spin spinning={isLoading}>
        {log && (
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="ID">{log.Id}</Descriptions.Item>

            <Descriptions.Item label="Дата">
              {dayjs(log.Timestamp).format('DD.MM.YYYY HH:mm:ss')}
            </Descriptions.Item>

            <Descriptions.Item label="Уровень">
              <Text strong>{log.Level ?? '—'}</Text>
            </Descriptions.Item>

            <Descriptions.Item label="Модуль">
              {log.Module ?? '—'}
            </Descriptions.Item>

            <Descriptions.Item label="Сообщение">
              <Paragraph>{log.Message ?? '—'}</Paragraph>
            </Descriptions.Item>

            <Descriptions.Item label="Exception">
              {log.Exception ? (
                <Paragraph type="danger">{log.Exception}</Paragraph>
              ) : (
                '—'
              )}
            </Descriptions.Item>

            <Descriptions.Item label="Properties">
              {propertiesData.length > 0 ? (
                <Table
                  showHeader={false}
                  size="small"
                  pagination={false}
                  dataSource={propertiesData}
                  columns={columns}
                />
              ) : (
                '—'
              )}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Spin>
    </Modal>
  );
};
